import jwt from 'jwt-decode'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { publicRoutes } from '~/AppRoutes'
import queryClient from '~/data/queryClient'
import { useGetUserPermissions } from '~/data/useData'
import {
  impersonateUser as impersonateUserApi,
  stopImpersonation as stopImpersonationApi,
} from '~/modules/auth/data/authApi'
import { useAppDispatch, useAppSelector } from '~/store'
import {
  changeIdentity,
  changePermissions,
  changeToken,
  changeUser,
  logoutUser,
  startImpersonation,
  stopImpersonation,
} from '~/store/slices/authSlice'
import { ICompany, IIdentity, IUser, JWTDecoded } from '~/utils/types'

export const useAuth = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { token, user, identity, permissions, impersonation } = useAppSelector(
    (store) => store.auth,
  )
  const isLogged = Boolean(token)
  const [isPermissionsLoaded, setIsPermissionsLoaded] = useState(false)

  const lastLogin = useMemo(() => {
    if (user?.last_login) {
      return new Date(user.last_login)
    }
    return null
  }, [user])

  const userId = user?._id ?? user?.userId
  const profile = user?.profile ?? 'guest'

  const isImpersonated = impersonation.isImpersonated
  const canImpersonate =
    !isImpersonated &&
    profile === 'manager' &&
    (user?.company as ICompany)?.master === true

  const isAssigned = useCallback(
    (permission: string): boolean => {
      if (!permission) return true
      if (!permissions) return false
      return permissions.some((permissionElement) => {
        if (permission === permissionElement) return true
        const permissionWithPrefix = `permission:${permission}`
        return permissionWithPrefix === permissionElement
      })
    },
    [permissions],
  )

  const updateToken = useCallback(
    (newToken: string) => {
      dispatch(changeToken(newToken))
    },
    [dispatch],
  )

  const updateUser = useCallback(
    (newUser: IUser) => {
      dispatch(changeUser(newUser))
    },
    [dispatch],
  )

  const updateIdentity = useCallback(
    (newIdentity: IIdentity) => {
      dispatch(changeIdentity(newIdentity))
    },
    [dispatch],
  )

  const updatePermissions = useCallback(
    (permissions: string[]) => {
      dispatch(changePermissions(permissions))
    },
    [dispatch],
  )

  const handleLogout = useCallback(() => {
    queryClient.clear()
    dispatch(logoutUser())
    navigate('/auth/login')
  }, [dispatch, navigate])

  const handleImpersonate = useCallback(
    async (targetUserId: string) => {
      const data = await impersonateUserApi(targetUserId)
      queryClient.clear()
      dispatch(
        startImpersonation({
          token: data.token,
          user: data.user,
          impersonatorId: data.impersonator_id,
        }),
      )
      navigate('/')
    },
    [dispatch, navigate],
  )

  const handleStopImpersonation = useCallback(async () => {
    const data = await stopImpersonationApi()
    queryClient.clear()
    dispatch(
      stopImpersonation({
        token: data.token,
        refresh_token: data.refresh_token,
        user: data.user,
      }),
    )
    navigate('/')
  }, [dispatch, navigate])

  const userPermissions = useGetUserPermissions(user)

  useEffect(() => {
    if (userPermissions?.isSuccess) {
      updatePermissions(userPermissions?.data || [])
      setIsPermissionsLoaded(true)
    }
  }, [updatePermissions, userPermissions.data, userPermissions.isSuccess])

  useEffect(() => {
    if (publicRoutes.some((route) => pathname.startsWith(route))) return

    if (!token) {
      if (pathname !== '/auth/login') {
        navigate('/auth/login', {
          state: {
            redirectUrl: pathname,
          },
        })
      }

      return
    }

    const decodedToken: JWTDecoded<IUser> = jwt(token)
    if (decodedToken.exp === -1) return

    const expiryTime = new Date(decodedToken.exp * 1000)
    const currentTime = new Date()
    if (expiryTime < currentTime) {
      handleLogout()
      toast.error('Your session has expired, please login again.', {
        toastId: 'logout-toast',
      })
    }
  }, [token, handleLogout, pathname, navigate, search])

  return {
    token,
    user,
    identity,
    userId,
    isAssigned,
    profile,
    isLogged,
    lastLogin,
    updateToken,
    updateUser,
    updateIdentity,
    handleLogout,
    isPermissionsLoaded,
    isImpersonated,
    canImpersonate,
    handleImpersonate,
    handleStopImpersonation,
    impersonation,
  }
}
